/**
 * Username validation utility
 * Rules:
 * - Only alphanumeric characters and underscores
 * - Cannot start or end with underscore
 * - Must be at least 3 characters long
 * - Automatically converts to lowercase
 */

export interface UsernameValidationResult {
  isValid: boolean;
  error?: string;
  sanitizedValue: string;
}

/**
 * Sanitizes username input by removing invalid characters and converting to lowercase
 */
export const sanitizeUsername = (input: string): string => {
  // Remove @ symbol if present, convert to lowercase, and keep only alphanumeric and underscores
  return input
    .replace('@', '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '');
};

/**
 * Validates username according to business rules
 */
export const validateUsername = (username: string): UsernameValidationResult => {
  const sanitized = sanitizeUsername(username);
  
  // Check if empty
  if (!sanitized.trim()) {
    return {
      isValid: false,
      error: "Username is required",
      sanitizedValue: sanitized
    };
  }
  
  // Check minimum length
  if (sanitized.length < 3) {
    return {
      isValid: false,
      error: "Username must be at least 3 characters",
      sanitizedValue: sanitized
    };
  }
  
  // Check if starts or ends with underscore
  if (sanitized.startsWith('_') || sanitized.endsWith('_')) {
    return {
      isValid: false,
      error: "Username cannot start or end with underscore",
      sanitizedValue: sanitized
    };
  }
  
  // Check if contains only valid characters (this should be redundant after sanitization, but good for validation)
  const validPattern = /^[a-z0-9_]+$/;
  if (!validPattern.test(sanitized)) {
    return {
      isValid: false,
      error: "Username can only contain letters, numbers, and underscores",
      sanitizedValue: sanitized
    };
  }
  
  return {
    isValid: true,
    sanitizedValue: sanitized
  };
};

/**
 * Formats username for display (adds @ prefix)
 */
export const formatUsernameForDisplay = (username: string): string => {
  if (!username) return '';
  return username.startsWith('@') ? username : `@${username}`;
};

/**
 * Removes @ prefix from username for storage
 */
export const removeUsernamePrefix = (username: string): string => {
  return username.replace('@', '');
};
